import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ShieldCheck } from 'lucide-react';
import { CHAT_REPORT_CONFIG } from '@draughts/shared';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { log } from '../../utils/logger';
import './ReportModal.css';

/** How long the confirmation stays up before the modal closes itself. */
const DONE_DISMISS_MS = 2500;

/** La forma di un errore come lo costruisce services/api.ts. */
export interface ReportApiError {
  status?: number;
  code?: string;
}

export interface ReportModalProps<R extends string> {
  /** Tutte le stringhe arrivano GIÀ tradotte: il guscio non conosce le chiavi. */
  title: string;
  intro: string;
  reasons: readonly R[];
  reasonLabel: (reason: R) => string;
  reasonGroupLabel: string;
  noteLabel: string;
  notePlaceholder: string;
  privacyHint: string;
  submitLabel: string;
  sendingLabel: string;
  doneLabel: string;
  /** Il messaggio incriminato, quando il flusso può citarne uno. */
  quote?: { author: string; text: string } | null;
  onSubmit: (reason: R, note?: string) => Promise<void>;
  /**
   * Da errore a frase tradotta. Sta fuori perché ogni flusso ha i suoi casi: la
   * chat può dire «il trascritto non è più verificabile», il profilo «quel
   * giocatore non esiste più», e nessuno dei due ha senso nell'altro.
   *
   * Può avere effetti: una segnalazione GIÀ in archivio è, per chi segnala,
   * l'esito che voleva, e i due chiamanti ne approfittano per avvisare chi li
   * ospita (mettere in muto l'avversario, non riproporre il pulsante) prima di
   * restituire la frase. Era già così nel `catch` da cui questo guscio è stato
   * estratto.
   */
  mapError: (err: ReportApiError) => string;
  onClose: () => void;
}

/**
 * Il guscio condiviso delle due modali di segnalazione.
 *
 * Qui sta tutto ciò che è uguale e che nessuno vuole correggere due volte: il
 * portale, la trappola del fuoco, il gesto «indietro», il radiogruppo, la nota
 * col contatore, e lo stato «inviata» che si chiude da sé. Quello che cambia —
 * i motivi, la citazione, la mappa degli errori, e soprattutto il testo sulla
 * privacy — lo passano i due chiamanti.
 *
 * Nessun listener per Escape, di proposito: `FullscreenPanel` ne registra già
 * uno su `window`, e una singola pressione chiuderebbe QUESTA modale e il
 * pannello dietro. Si chiude con la ×, col fondale o col gesto indietro.
 */
export function ReportModal<R extends string>({
  title,
  intro,
  reasons,
  reasonLabel,
  reasonGroupLabel,
  noteLabel,
  notePlaceholder,
  privacyHint,
  submitLabel,
  sendingLabel,
  doneLabel,
  quote = null,
  onSubmit,
  mapError,
  onClose,
}: ReportModalProps<R>) {
  const { t } = useTranslation();
  // Overlay annidato: su mobile il gesto indietro chiude PRIMA questa e poi il
  // pannello sotto (pila LIFO). Va prima di ogni early return.
  useOverlayBackButton(true, onClose);

  const [reason, setReason] = useState<R | null>(null);
  const [note, setNote] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!sent) return;
    const timeout = setTimeout(onClose, DONE_DISMISS_MS);
    return () => clearTimeout(timeout);
  }, [sent, onClose]);

  const handleSubmit = async () => {
    if (!reason || isBusy) return;
    setIsBusy(true);
    setError(null);
    try {
      await onSubmit(reason, note.trim() || undefined);
      setSent(true);
    } catch (e) {
      const err = e as ReportApiError;
      log.warn('ReportModal', 'report failed', { status: err.status, code: err.code });
      setError(mapError(err));
    } finally {
      setIsBusy(false);
    }
  };

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva. Senza `onClose`: l'Escape qui NON lo gestisce questo
   * componente (vedi il commento in testa), e passarlo all'hook vorrebbe dire
   * chiudere due volte.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(true);

  return createPortal(
    <div className="report-modal-overlay" onClick={onClose}>
      {/* La modale è figlia React del suo chiamante, quindi i clic risalirebbero
          fino al fondale del pannello e lo chiuderebbero sotto di noi. */}
      <div
        className="report-modal-box"
        onClick={(e) => e.stopPropagation()}
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={title}
      >
        <div className="report-modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="report-modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {sent ? (
          <div className="report-modal-done" role="status">
            <ShieldCheck size={22} aria-hidden="true" />
            <p>{doneLabel}</p>
          </div>
        ) : (
          <>
            <p className="report-modal-intro">{intro}</p>

            {quote && (
              <div className="report-modal-quote">
                <span className="report-modal-quote-author">{quote.author}</span>
                <span className="report-modal-quote-text" dir="auto">{quote.text}</span>
              </div>
            )}

            <div className="report-modal-reasons" role="radiogroup" aria-label={reasonGroupLabel}>
              {reasons.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={reason === r}
                  className={`report-modal-reason ${reason === r ? 'selected' : ''}`}
                  onClick={() => setReason(r)}
                  disabled={isBusy}
                >
                  {reasonLabel(r)}
                </button>
              ))}
            </div>

            <label className="report-modal-note-label" htmlFor="report-modal-note">
              {noteLabel}
            </label>
            <textarea
              id="report-modal-note"
              className="report-modal-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={notePlaceholder}
              maxLength={CHAT_REPORT_CONFIG.MAX_NOTE_LENGTH}
              rows={3}
              disabled={isBusy}
            />
            <div className="report-modal-note-count">
              {note.length}/{CHAT_REPORT_CONFIG.MAX_NOTE_LENGTH}
            </div>

            <p className="report-modal-hint">{privacyHint}</p>

            {error && <p className="report-modal-error" role="alert">{error}</p>}

            <div className="report-modal-actions">
              <button type="button" className="report-modal-cancel" onClick={onClose} disabled={isBusy}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="report-modal-submit"
                onClick={handleSubmit}
                disabled={!reason || isBusy}
              >
                {isBusy ? sendingLabel : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ReportModal;
